export const surgery2Module = {
  id: "surgery2",
  title: "Thyroid Surgery: Part 2",
  sections: [
    {
      type: "text",
      title: "Parathyroid Glands at Risk",
      content: `
        <p>The parathyroid glands are intimately related to the thyroid and are at significant risk during thyroidectomy. Their inadvertent removal or devascularisation is the most common cause of post-operative hypocalcaemia.</p>

        <div class="highlight-box">
          <p><strong>Anatomy and blood supply</strong></p>
          <ul>
            <li>Usually <strong>4 glands</strong> (each ~35–40 mg; yellowish-brown; size of a split pea); ~13% have a 5th supernumerary gland</li>
            <li><strong>Superior parathyroids</strong> (from 4th pharyngeal pouch): more constant position — posterior to upper thyroid lobe, near the cricothyroid joint</li>
            <li><strong>Inferior parathyroids</strong> (from 3rd pharyngeal pouch, same as thymus): more variable — near the lower thyroid pole, thyrothymic ligament, or inside the thymus (ectopic)</li>
            <li>Blood supply: predominantly <span class="purple-term">inferior thyroid artery</span> (both superior and inferior parathyroids); must be preserved during thyroid artery ligation</li>
            <li>Technique: ligate inferior thyroid artery <strong>branches at the thyroid capsule</strong>, not the main trunk, to preserve parathyroid blood supply</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Types of hypoparathyroidism after thyroid surgery</strong></p>
          <ul>
            <li><span class="purple-term">Transient hypoparathyroidism</span> (most common): bruising, oedema, or temporary ischaemia of parathyroids → resolves within days–6 months as vascularity recovers; treated with calcium + vitamin D supplementation</li>
            <li><span class="purple-term">Permanent hypoparathyroidism</span>: all four glands devascularised or inadvertently excised → lifelong calcium and calcitriol replacement; risk:
              <ul>
                <li>After hemithyroidectomy: &lt;1%</li>
                <li>After total thyroidectomy: ~1–2% with experienced surgeon; higher with central neck dissection</li>
                <li>After redo surgery: significantly increased risk</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Parathyroid autotransplantation</strong></p>
          <ul>
            <li>If a parathyroid gland is inadvertently removed or appears devascularised (blue/ischaemic colour), it can be autotransplanted</li>
            <li>Technique: gland minced into 1 mm fragments → implanted into a pocket in the <strong>sternocleidomastoid muscle</strong> (brachioradialis if revision surgery or previous neck irradiation)</li>
            <li>Success rate: ~75–80% of autotransplanted glands become functional (confirmed by elevated PTH on the ipsilateral forearm vein)</li>
            <li>Confirmed viable parathyroid tissue can be identified using frozen section or autofluorescence (parathyroid tissue autofluoresces under near-infrared light)</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Parathyroid Origin Mnemonic</div>
          <div class="mnemonic-content"><strong>Superior from 4th pouch → Stay Superior (consistent position)</strong><br/><strong>Inferior from 3rd pouch (with thymus) → Inferior and variable (can descend into chest with thymus)</strong><br/>Because the inferior parathyroids travel further during embryological migration, they end up in a more variable position — hence ectopic inferior parathyroids are more common.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "During a total thyroidectomy for Graves' disease, one inferior parathyroid gland is accidentally excised. It appears pale and viable. What should the surgeon do?",
      options: [
        "Discard it — one gland is redundant given the other three remain",
        "Autotransplant it: mince into 1 mm fragments and implant into a sternocleidomastoid muscle pocket",
        "Reimplant it back in its original anatomical site with a clip to hold it in place",
        "Send it for frozen section to confirm it is parathyroid before any further action",
      ],
      correct: 1,
      explanation:
        "An inadvertently removed or devascularised parathyroid gland should be autotransplanted to maximise the chance of preserving parathyroid function. The standard technique is to mince the gland into 1–2 mm fragments and place them into individual pockets in the sternocleidomastoid muscle. Approximately 75–80% of autotransplanted glands regain function. The gland should NOT simply be discarded — even one remaining functional parathyroid gland can prevent permanent hypoparathyroidism. Near-infrared autofluorescence can help confirm the tissue is parathyroid before implantation.",
    },
    {
      type: "question",
      text: "A patient had total thyroidectomy and central neck dissection for PTC 6 hours ago. Her serum calcium is 1.9 mmol/L (reference: 2.1–2.6). She has perioral tingling and her hands are cramping. What sign would you elicit at the bedside to confirm the diagnosis, and what is the treatment?",
      options: [
        "Trousseau's sign (carpal spasm on arm cuff inflation) confirms hypocalcaemia; treat with IV calcium gluconate if symptomatic",
        "Chvostek's sign (facial twitch on tapping) rules in hypocalcaemia; treat with oral calcium carbonate alone",
        "Lhermitte's sign rules in hypomagnesaemia; treat with IV magnesium",
        "Hoffmann's sign confirms tetany; treat with hyperventilation to raise pH",
      ],
      correct: 0,
      explanation:
        "Post-operative hypocalcaemia is the most common complication of total thyroidectomy. Symptoms include perioral paraesthesias, carpopedal spasm, and tetany. Trousseau's sign (inflation of BP cuff 20 mmHg above systolic for 3 minutes → carpal spasm from ischaemia-induced hyperexcitability) is more specific than Chvostek's (tapping over facial nerve → ipsilateral facial twitch, which can occur in 25% of normal individuals). Management: symptomatic/severe hypocalcaemia → IV calcium gluconate (10 mL of 10% solution over 10 minutes); mild/asymptomatic → oral calcium carbonate + calcitriol (active vitamin D).",
    },
    {
      type: "text",
      title: "Post-operative Complications",
      content: `
        <p>Complications of thyroid surgery range from immediate life-threatening emergencies to delayed functional problems. Timing guides assessment.</p>

        <div class="highlight-box">
          <p><strong>Post-operative haematoma — most dangerous early complication</strong></p>
          <ul>
            <li>Occurs within <strong>24 hours</strong> of surgery (majority within 4 hours)</li>
            <li>Expanding haematoma in the confined thyroid bed → compresses the trachea → airway obstruction → death if not treated immediately</li>
            <li>Presentation: neck swelling, stridor, dyspnoea, oxygen desaturation</li>
            <li><span class="purple-term">Management — do NOT wait to transfer to theatre:</span>
              <ol>
                <li>Immediately open the wound at the bedside (remove clips/sutures) to decompress the haematoma</li>
                <li>Summon anaesthetist and arrange theatre for formal re-exploration and haemostasis</li>
              </ol>
            </li>
            <li>Every thyroid post-operative ward must have a suture/clip removal kit at the bedside</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Hypocalcaemia (hypoparathyroidism)</strong></p>
          <ul>
            <li>Most common complication after total thyroidectomy — may affect up to 20–30% transiently</li>
            <li>Monitor serum calcium at <strong>4 hours and 12–24 hours</strong> post-operatively</li>
            <li>PTH at 4–6 hours: PTH &lt;1.2 pmol/L is predictive of significant post-operative hypocalcaemia → allows early calcium supplementation</li>
            <li>Treatment:
              <ul>
                <li>Asymptomatic mild: oral calcium carbonate (1–2 g/day) + calcitriol (0.25–0.5 mcg/day)</li>
                <li>Symptomatic/severe (tetany, seizure, QTc prolongation): IV calcium gluconate 10% bolus ± infusion</li>
                <li>Check magnesium (hypomagnesaemia impairs PTH secretion and is refractory to calcium alone)</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Other post-operative complications</strong></p>
          <ul>
            <li><strong>Wound infection</strong> (uncommon; clean surgery; prophylactic antibiotics not always used)</li>
            <li><strong>Chyle leak</strong>: injury to thoracic duct during left lateral neck dissection → milky drain fluid; treat with low-fat diet → medium-chain triglyceride diet → surgical ligation if not resolving</li>
            <li><strong>Horner's syndrome</strong>: injury to the cervical sympathetic chain (ptosis, miosis, anhidrosis) — more common in extensive lateral neck dissection</li>
            <li><strong>Seroma</strong>: fluid collection in the thyroid bed — usually self-resolving</li>
            <li><strong>Tracheomalacia</strong> (very rare): tracheal cartilage softening from chronic pressure → tracheal collapse after goitre removal; may require tracheal stenting or tracheostomy; anaesthetist anticipates this preoperatively</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Post-operative voice assessment</strong></p>
          <ul>
            <li>Laryngoscopy if patient is hoarse at discharge or at 1 week post-op</li>
            <li>Unilateral palsy: observe for 6 months; voice therapy; medialisation if persistent beyond 6–12 months</li>
            <li>Bilateral palsy: tracheostomy followed by staged surgical procedures (posterior cordotomy, arytenoidectomy) to widen the glottis</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient is on the thyroid surgery ward 3 hours after total thyroidectomy. A nurse calls to say the patient is becoming increasingly breathless and the neck appears swollen. What is the immediate priority?",
      options: [
        "Give IV furosemide to reduce oedema and monitor oxygen saturations",
        "Urgently open the neck wound at the bedside to decompress the haematoma — do not wait for theatre",
        "Arrange emergency CT neck to confirm haematoma before any intervention",
        "Call anaesthetics to perform emergency tracheostomy",
      ],
      correct: 1,
      explanation:
        "Expanding post-operative neck haematoma causing tracheal compression is a surgical emergency. Transfer to theatre takes time the patient does not have — tracheal obstruction can be fatal within minutes. The wound must be opened immediately at the bedside: remove skin clips or sutures to decompress the haematoma and relieve airway pressure. This buys time for the anaesthetist to secure the airway and for formal re-exploration in theatre. Every thyroid ward must have bedside clip-removal equipment for exactly this reason. CT scanning would be a dangerous delay.",
    },
    {
      type: "text",
      title: "Radioactive Iodine After Surgery",
      content: `
        <p>Radioactive iodine (I-131) plays a central role in the post-surgical management of differentiated thyroid cancers (PTC and FTC). It is not used in MTC (C cells lack the sodium-iodide symporter, NIS).</p>

        <div class="highlight-box">
          <p><strong>Why RAI is given after surgery</strong></p>
          <ul>
            <li><strong>Remnant ablation</strong>: total thyroidectomy always leaves a small amount of normal thyroid tissue in the thyroid bed; RAI destroys this remnant → allows thyroglobulin to be used as a sensitive tumour marker (Tg should be undetectable if no tumour remains)</li>
            <li><strong>Adjuvant therapy</strong>: destroys microscopic residual cancer cells at the surgical bed or nodal deposits not removed at surgery</li>
            <li><strong>Treatment of metastases</strong>: RAI-avid metastases (lung, bone) can be treated with therapeutic doses</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Who receives RAI after thyroidectomy?</strong></p>
          <ul>
            <li>Stratified by risk (ATA/ETA guidelines):
              <ul>
                <li><span class="purple-term">Low risk</span> (PTC ≤4 cm, intrathyroidal, no nodes, no metastases): RAI optional or omitted, especially after hemithyroidectomy</li>
                <li><span class="purple-term">Intermediate/high risk</span> (extrathyroidal extension, nodal disease, vascular invasion, distant metastases): RAI indicated</li>
                <li>FTC with capsular/vascular invasion: RAI recommended</li>
                <li>All patients with gross residual disease or distant metastases</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>TSH stimulation before RAI</strong></p>
          <ul>
            <li>RAI uptake by thyroid/cancer cells depends on NIS expression, which is TSH-dependent</li>
            <li>TSH must be <strong>&gt;30 mIU/L</strong> before RAI administration</li>
            <li>Two methods:
              <ul>
                <li>Levothyroxine <strong>withdrawal</strong> for 4–6 weeks → symptomatic hypothyroidism but achieves TSH rise</li>
                <li>Recombinant human TSH (thyrotropin alfa / Thyrogen) injection × 2 days → TSH rises without levothyroxine withdrawal; avoids hypothyroid burden; preferred in patients with cardiovascular disease or poor tolerance of hypothyroidism</li>
              </ul>
            </li>
            <li>Low-iodine diet for 1–2 weeks before RAI (minimise competing stable iodine in the diet)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Post-RAI considerations</strong></p>
          <ul>
            <li>Post-therapy whole-body scan (WBS) at 3–5 days: visualises RAI-avid tissue anywhere in the body</li>
            <li>Resume levothyroxine: start at TSH-suppressive dose for cancer patients (see pharmacology module)</li>
            <li>Radiation precautions: avoid close contact with young children and pregnant women for ~1 week</li>
            <li>Salivary gland inflammation (sialadenitis): a common side effect; sucking on sour sweets stimulates salivary flow to reduce iodine retention in the glands</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient had total thyroidectomy for a 2.5 cm PTC with no extra-thyroidal extension and no lymph node involvement. Levothyroxine has been withheld. What TSH level is required before RAI ablation, and why?",
      options: [
        "TSH must be <0.1 mIU/L — low TSH prevents thyrotoxicosis during RAI",
        "TSH must be >30 mIU/L — high TSH maximally upregulates NIS expression in any residual thyroid/cancer cells, enabling adequate RAI uptake",
        "TSH level is irrelevant — RAI uptake is purely dependent on the I-131 dose",
        "TSH must be 0.5–2 mIU/L — the normal therapeutic range for post-operative patients",
      ],
      correct: 1,
      explanation:
        "The sodium-iodide symporter (NIS), which transports iodine (and I-131) into thyroid and cancer cells, is directly regulated by TSH. High TSH (>30 mIU/L) maximally stimulates NIS expression in any residual thyroid tissue and differentiated cancer cells, enabling sufficient I-131 uptake for ablation and imaging. TSH elevation is achieved either by withdrawing levothyroxine for 4–6 weeks (endogenous TSH rises as hypothyroidism develops) or by injecting recombinant human TSH (thyrotropin alfa) — the latter avoids the symptomatic burden of hypothyroidism.",
    },
    {
      type: "text",
      title: "Long-term Follow-up After Thyroid Cancer Surgery",
      content: `
        <p>After surgery ± RAI, patients with differentiated thyroid cancer require structured long-term surveillance to detect recurrence early.</p>

        <div class="highlight-box">
          <p><strong>Thyroglobulin (Tg) — tumour marker for PTC and FTC</strong></p>
          <ul>
            <li>Tg is produced only by thyroid follicular cells (including differentiated cancer cells)</li>
            <li>After total thyroidectomy + RAI ablation: Tg should be <strong>undetectable</strong> (&lt;0.1–0.2 ng/mL) — any detectable Tg suggests residual or recurrent disease</li>
            <li>Tg is measured on <strong>TSH stimulation</strong> (either after levothyroxine withdrawal or after thyrotropin alfa) — stimulated Tg is more sensitive than basal Tg</li>
            <li><span class="purple-term">Anti-Tg antibodies</span> (present in ~25% of patients): interfere with Tg immunoassay → false low/undetectable result; anti-Tg antibodies themselves serve as a surrogate marker (rising titre = possible recurrence)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>TSH suppression therapy</strong></p>
          <ul>
            <li>TSH stimulates growth of differentiated thyroid cancer cells; suppressing TSH reduces cancer stimulation</li>
            <li>Levothyroxine dose titrated to achieve TSH suppression:
              <ul>
                <li><span class="purple-term">High-risk disease</span>: TSH &lt;0.1 mIU/L (full suppression) → risk of AF and osteoporosis with long-term over-treatment</li>
                <li><span class="purple-term">Low-risk / remission</span>: TSH 0.5–2.0 mIU/L (mild suppression or low normal)</li>
              </ul>
            </li>
            <li>Level of suppression individualised based on recurrence risk vs side-effect risk (cardiac, bone)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Imaging surveillance</strong></p>
          <ul>
            <li><strong>Neck USS</strong>: 6–12 months post-RAI, then annually for 3–5 years; assesses thyroid bed and cervical lymph nodes for nodal recurrence</li>
            <li><strong>Whole-body RAI scan</strong>: not done routinely in low-risk; used if Tg rises or suspicion of distant recurrence</li>
            <li><strong>CT/PET scan</strong>: for RAI-negative but Tg-positive disease (dedifferentiated tumour no longer takes up RAI)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Medullary thyroid carcinoma (MTC) follow-up</strong></p>
          <ul>
            <li>Tg is NOT useful in MTC (C cells do not produce thyroglobulin)</li>
            <li>Tumour markers: <span class="purple-term">calcitonin</span> and <span class="purple-term">CEA (carcinoembryonic antigen)</span>
              <ul>
                <li>Calcitonin: most sensitive marker; doubling time correlates with prognosis (&lt;6 months → aggressive disease)</li>
                <li>CEA: less specific but rising CEA with stable calcitonin may indicate dedifferentiation</li>
              </ul>
            </li>
            <li>Genetic testing: all patients with MTC should undergo RET mutation testing; index cases with germline RET → genetic counselling + screening of first-degree relatives</li>
            <li>MEN2 surveillance: MEN2A patients → annual phaeo screen (24-hour urine catecholamines/plasma metanephrines) + calcium/PTH for HPT</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Post-op Follow-up Comparison</div>
          <div class="mnemonic-content"><strong>PTC/FTC:</strong> Thyroglobulin + anti-Tg + neck USS + TSH suppression + RAI scan if indicated<br/><strong>MTC:</strong> Calcitonin + CEA + neck USS + RET mutation testing + phaeo screen (if MEN2)</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "Six months after total thyroidectomy and RAI ablation for PTC, a patient has a stimulated thyroglobulin of 4.2 ng/mL (should be <0.1 ng/mL). Anti-Tg antibodies are negative. What does this indicate and what is the next step?",
      options: [
        "This is a normal result — thyroglobulin takes 12 months to become fully undetectable",
        "Residual or recurrent differentiated thyroid cancer — arrange neck USS and consider whole-body RAI scan",
        "Anti-Tg antibody interference — repeat Tg with a different assay",
        "TSH stimulation is not reliable — this result should be disregarded",
      ],
      correct: 1,
      explanation:
        "After total thyroidectomy and RAI ablation, stimulated thyroglobulin should be <0.1–0.2 ng/mL. A detectable stimulated Tg of 4.2 ng/mL in the absence of anti-Tg antibody interference is a significant concern for residual or recurrent disease. The next step is neck USS to look for cervical lymph node recurrence (the most common site of PTC recurrence). If USS is negative but Tg remains elevated, a whole-body RAI scan (diagnostic or therapeutic activity) is performed to look for RAI-avid metastases. CT/FDG-PET is used when the tumour is RAI-negative (dedifferentiated).",
    },
    {
      type: "question",
      text: "A patient treated with total thyroidectomy for MTC has a post-operative calcitonin of 120 pg/mL (reference <10 pg/mL). Germline testing reveals a RET C634F mutation. What follow-up action is required beyond imaging?",
      options: [
        "Start RAI — residual calcitonin-secreting tissue will be ablated with I-131",
        "Offer genetic counselling and RET mutation testing to first-degree relatives — germline RET is hereditary (MEN2A)",
        "No further action — residual calcitonin is expected and monitoring alone is sufficient",
        "Refer to immunology — calcitonin elevation after MTC surgery indicates autoimmune MTC recurrence",
      ],
      correct: 1,
      explanation:
        "RET C634F is a codon 634 mutation characteristic of MEN2A (associated with MTC + phaeochromocytoma + primary hyperparathyroidism). Any patient with a germline RET mutation has hereditary MTC — an autosomal dominant condition. All first-degree relatives (parents, children, siblings) must be offered RET genetic testing: those who carry the mutation should undergo prophylactic thyroidectomy (timing based on mutation risk level) and annual phaeo screening. RAI is not used in MTC — C cells lack NIS. Elevated post-operative calcitonin requires imaging to look for residual/metastatic disease, not reassurance.",
    },
  ],
};
